import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as RC from 'recharts';
import { Trash2, Shield, CheckCircle2, ShieldAlert, ClipboardList, Scale, Clock, Play, TrendingUp, AlertTriangle, Sparkles, Pencil, FilePlus2, Copy as CopyIcon, Upload, X, Lock, Building2, ClipboardCheck, Square, CheckSquare, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { SECTIONS } from '../lib/sectionTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const M = SECTIONS.management;

// Mirror /backend/routes/preparation.py — kept in sync with the seeded prep
// guide library so admin authoring stays consistent with the reference set.
const PREP_MODALITIES = ['General', 'CT', 'Ultrasound', 'X-ray', 'DEXA', 'Dental', 'Special Patient Groups'];
const PREP_SUBCATEGORIES = ['Diagnostic scan', 'Image-guided procedure', 'Procedure', 'Special group', 'Overview', 'Other'];
// Booking-alert tags shown to reception — mirrors the flags used in seed_prep_guides.py.
const PREP_TAG_OPTIONS = [
  'FASTING', 'CT-CONTRAST-RISK', 'METFORMIN', 'RENAL-FUNCTION-REQUIRED',
  'THYROID-CHECK', 'PREGNANCY-CHECK', 'DRIVER-REQUIRED', 'FULL-BLADDER',
  'BLOOD-THINNER', 'DIABETIC-STEROID-WARNING', 'PROCEDURE-CONSENT',
];

function Modal({ title, children, onClose, size = 'sm' }) {
  const widths = { sm: 'max-w-lg', md: 'max-w-2xl', lg: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className={`bg-card border border-border rounded-2xl ${widths[size]} w-full p-5 max-h-[90vh] overflow-y-auto`}>
        <h3 className="font-['Outfit'] text-lg font-bold text-foreground mb-3">{title}</h3>
        {children}
      </div>
    </div>
  );
}

export default function ManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role === 'admin' || user?.role === 'superuser';

  const [cronOpen, setCronOpen] = useState(false);

  useEffect(() => {
    if (!canManage) navigate('/');
  }, [canManage, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="management-page">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Greeting — Admin Hub welcome */}
        <div className="mb-8" data-testid="mgmt-greeting">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <div className="inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: M.color }} />
              <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: M.color }}>Admin Hub</span>
            </div>
            <CronHealthPill open={cronOpen} onToggle={() => setCronOpen(o => !o)} />
          </div>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Hello, <span style={{ color: M.color }}>{user.name?.split(' ')[0]}</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
            Quick access to practice settings, audit trails, governance and the incident register.
          </p>
        </div>

        {/* Quick tools — the four things this page is actually for */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="mgmt-tools">
          <Tool to="/practice-settings" icon={Building2} label="Practice settings" sub="ABN, LSPN, HPI-O, address" color="#42CDD7" />
          <Tool to="/dashboard/logins" icon={ShieldAlert} label="Login activity" sub="Audit trail & alerts" color="#DC2626" />
          <Tool to="/sops/governance" icon={Shield} label="Governance dashboard" sub="SOP review sign-off" color="#7C3AED" />
          <Tool to="/disputes" icon={Scale} label="Conflict register" sub="Staff disputes & mediation" color="#7C3AED" />
        </div>

        {/* Incident register — compact triage panel; full register at /incidents */}
        <IncidentsDashboardWidget />

        {/* Monthly walk-through audit — mobile-first tap-through checklist */}
        <MonthlyAuditWidget />

        {/* Cron status — collapsed by default; expanded only when the pill is clicked */}
        {cronOpen && <CronStatusWidget />}
      </div>
    </div>
  );
}

function Tool({ to, icon: Icon, label, sub, color }) {
  const c = color || M.color;
  return (
    <Link to={to} data-testid={`mgmt-tool-${to.replace(/\W+/g, '-')}`}
      className="flex items-start gap-3 p-4 bg-card border border-border rounded-2xl hover:shadow-sm transition-all">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${c}15`, color: c }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-['Outfit'] text-sm font-semibold text-foreground">{label}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>
      </div>
    </Link>
  );
}

function Input({ label, type = 'text', value, onChange, required, testid, minLength }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required} minLength={minLength} data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
    </label>
  );
}

function CronHealthPill({ open, onToggle }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/cron/status`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  // Health rule:
  //  • red  → scheduler stopped, or any last_run.status === 'error'
  //  • amber → any job has next_run in the past or never ran
  //  • green → everything healthy
  const lastRuns = data.last_runs || {};
  const jobs = data.jobs || [];
  const anyFailed = Object.values(lastRuns).some(r => r?.status && r.status !== 'success');
  const now = Date.now();
  const anyOverdue = jobs.some(j => !j.next_run || (new Date(j.next_run).getTime() < now - 60_000));
  let level = 'ok';
  if (!data.running || anyFailed) level = 'error';
  else if (anyOverdue) level = 'warn';

  const palette = {
    ok:    { dot: '#16A34A', bg: '#16A34A15', label: 'Cron healthy' },
    warn:  { dot: '#D97706', bg: '#D9770615', label: 'Cron stale' },
    error: { dot: '#DC2626', bg: '#DC262615', label: 'Cron failing' },
  }[level];

  return (
    <button onClick={onToggle} data-testid="cron-health-pill"
      title={`${jobs.length} scheduled job${jobs.length === 1 ? '' : 's'} · click to ${open ? 'collapse' : 'expand'}`}
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border self-start"
      style={{ backgroundColor: palette.bg, borderColor: `${palette.dot}40`, color: palette.dot }}>
      <span className="relative flex w-2 h-2">
        {level === 'ok' && <span className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: palette.dot, opacity: 0.4 }} />}
        <span className="relative inline-flex rounded-full w-2 h-2" style={{ backgroundColor: palette.dot }} />
      </span>
      {palette.label}
      <Clock className="w-3 h-3 opacity-70" />
    </button>
  );
}

function CronStatusWidget() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/cron/status`, { withCredentials: true });
      setData(r.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const runNow = async (id) => {
    if (!window.confirm(`Run '${id}' now? This sends real emails / hits real endpoints.`)) return;
    setBusy(id);
    try {
      await axios.post(`${API}/cron/run-now/${id}`, {}, { withCredentials: true });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    setBusy('');
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : '—';
  if (!data) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="mgmt-cron-status">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="w-4 h-4" style={{ color: M.color }} />
        <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Scheduled jobs</h2>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ml-2 ${data.running ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}
              data-testid="cron-running-state">
          {data.running ? 'Running' : 'Stopped'}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Background cron runs automatically. Use "Run now" to test or catch up manually.</p>

      <div className="grid sm:grid-cols-2 gap-3">
        {(data.jobs || []).map((j) => {
          const last = (data.last_runs || {})[j.id];
          return (
            <div key={j.id} className="border border-border rounded-xl p-4" data-testid={`cron-job-${j.id}`}>
              <p className="text-sm font-semibold text-foreground">{j.name}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Next run: <span className="text-foreground">{fmt(j.next_run)}</span></p>
              <p className="text-[11px] text-muted-foreground">
                Last run: {last ? (
                  <span className={last.status === 'success' ? 'text-green-600' : 'text-red-600'}>{last.status} · {fmt(last.ran_at)}</span>
                ) : <span className="text-muted-foreground">never</span>}
              </p>
              <button onClick={() => runNow(j.id)} disabled={busy === j.id} data-testid={`cron-run-${j.id}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-border hover:bg-accent disabled:opacity-40">
                <Play className="w-3 h-3" /> {busy === j.id ? 'Running…' : 'Run now'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function parseMarkdownSteps(md) {
  if (!md) return [];
  const lines = md.split('\n');
  const steps = [];
  let current = { title: 'Overview', body: [] };
  for (const ln of lines) {
    const m = ln.match(/^##\s+(.+?)\s*$/);
    if (m) {
      if (current.body.length || steps.length) {
        steps.push({ title: current.title, body: current.body.join('\n').trim() });
      }
      current = { title: m[1].trim(), body: [] };
    } else {
      current.body.push(ln);
    }
  }
  if (current.body.length || steps.length === 0) {
    steps.push({ title: current.title, body: current.body.join('\n').trim() });
  }
  return steps.filter(s => s.title || s.body);
}

export function CustomPrepManager() {
  const [guides, setGuides] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/prep-guides`, { withCredentials: true });
      setGuides(r.data.filter(g => g.is_custom));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load custom prep guides');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onSaved = () => { setEditing(null); load(); };

  const onDelete = async (g) => {
    if (!window.confirm(`Delete "${g.title}"?`)) return;
    try {
      await axios.delete(`${API}/prep-guides/${g.guide_id}`, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const onDuplicate = async (g) => {
    try {
      await axios.post(`${API}/prep-guides`, {
        title: `${g.title} (copy)`,
        modality: g.modality,
        subcategory: g.subcategory || '',
        content: g.content,
        alerts: g.alerts || [],
        sort_order: null,
      }, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="mgmt-custom-prep">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" style={{ color: M.color }} />
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Custom prep guide library</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Patient-facing prep notes authored by your team. Appear in <code className="font-mono bg-muted px-1 rounded">/preparation</code> alongside seeded i3 guides.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setImporting(true)} data-testid="custom-prep-import-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border"
            style={{ borderColor: `${M.color}40`, color: M.color }}>
            <Upload className="w-4 h-4" /> Import markdown
          </button>
          <button onClick={() => setEditing('new')} data-testid="custom-prep-new-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
            style={{ backgroundColor: M.color }}>
            <FilePlus2 className="w-4 h-4" /> New prep guide
          </button>
        </div>
      </div>

      {err && <p className="text-xs text-red-500 mb-3" data-testid="custom-prep-error">{err}</p>}

      {loading ? (
        <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
      ) : guides.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-border rounded-xl" data-testid="custom-prep-empty">
          <p className="text-sm text-muted-foreground">No custom prep guides yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Click <strong>New prep guide</strong> to add one — it becomes available to every staff member in <code className="font-mono">/preparation</code>.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="custom-prep-list">
          {guides.map(g => (
            <li key={g.guide_id} data-testid={`custom-prep-row-${g.guide_id}`}
              className="grid grid-cols-[1fr_auto] gap-3 items-center px-4 py-3 border border-border rounded-xl hover:bg-accent/30 transition-colors">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{g.title}</p>
                <p className="text-[11px] text-muted-foreground">
                  {g.modality}{g.subcategory ? ` · ${g.subcategory}` : ''}
                  {(g.alerts || []).length > 0 ? ` · ${g.alerts.length} alert${g.alerts.length === 1 ? '' : 's'}` : ''}
                  {g.created_by ? ` · by ${g.created_by}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => onDuplicate(g)} data-testid={`custom-prep-duplicate-${g.guide_id}`}
                  title="Duplicate" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
                  <CopyIcon className="w-4 h-4" />
                </button>
                <button onClick={() => setEditing(g)} data-testid={`custom-prep-edit-${g.guide_id}`}
                  title="Edit" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => onDelete(g)} data-testid={`custom-prep-delete-${g.guide_id}`}
                  title="Delete" className="p-2 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-accent">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <CustomPrepEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}

      {importing && (
        <BulkPrepImportModal
          onClose={() => setImporting(false)}
          onDone={() => { setImporting(false); load(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk Markdown Prep Guide Import
// Admin picks 1+ .md files, each becomes a new custom prep guide. Title is
// detected from first `# Heading` or filename; default modality/subcategory/
// alerts apply to all files in the batch.
// ─────────────────────────────────────────────────────────────────────────────
function BulkPrepImportModal({ onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [modality, setModality] = useState(PREP_MODALITIES[0]);
  const [subcategory, setSubcategory] = useState(PREP_SUBCATEGORIES[0]);
  const [alerts, setAlerts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [finished, setFinished] = useState(false);

  const includedCount = files.filter(f => f.include).length;

  const detectTitle = (filename, content) => {
    const h1 = content.split('\n').find(ln => /^#\s+.+/.test(ln));
    if (h1) return h1.replace(/^#\s+/, '').trim();
    const base = filename.replace(/\.(md|markdown|txt)$/i, '').replace(/[-_]+/g, ' ');
    return base.replace(/\b\w/g, c => c.toUpperCase()).trim();
  };

  const onPick = async (fileList) => {
    const arr = Array.from(fileList);
    const parsed = await Promise.all(arr.map(async f => {
      const text = await f.text();
      return {
        name: f.name,
        title: detectTitle(f.name, text),
        content: text,
        include: true,
        status: 'pending',
        error: '',
      };
    }));
    setFiles(prev => [...prev, ...parsed]);
  };

  const updateFile = (idx, patch) => setFiles(fs => fs.map((f, i) => i === idx ? { ...f, ...patch } : f));
  const removeFile = (idx) => setFiles(fs => fs.filter((_, i) => i !== idx));
  const toggleAlert = (t) => setAlerts(a => a.includes(t) ? a.filter(x => x !== t) : [...a, t]);

  const runImport = async () => {
    setImporting(true);
    const next = [...files];
    for (let i = 0; i < next.length; i++) {
      if (!next[i].include) continue;
      if (!next[i].title.trim() || !next[i].content.trim()) {
        next[i] = { ...next[i], status: 'failed', error: 'Empty title or content' };
        setFiles([...next]);
        continue;
      }
      try {
        await axios.post(`${API}/prep-guides`, {
          title: next[i].title.trim(),
          modality,
          subcategory: subcategory === 'Other' ? '' : subcategory,
          content: next[i].content,
          alerts,
          sort_order: null,
        }, { withCredentials: true });
        next[i] = { ...next[i], status: 'success' };
      } catch (e) {
        next[i] = { ...next[i], status: 'failed', error: e?.response?.data?.detail || 'Failed' };
      }
      setFiles([...next]);
    }
    setImporting(false);
    setFinished(true);
  };

  const successCount = files.filter(f => f.status === 'success').length;
  const failedCount = files.filter(f => f.status === 'failed').length;

  return (
    <Modal title="Bulk-import prep guides from markdown" onClose={finished ? onDone : onClose} size="lg">
      <div className="space-y-4" data-testid="bulk-prep-import-modal">
        {!finished ? (
          <>
            <p className="text-xs text-muted-foreground">
              Drop in one or more <code className="font-mono bg-muted px-1 rounded">.md</code> files — each becomes a new custom prep guide. Title is auto-detected from the first <code className="font-mono bg-muted px-1 rounded"># Heading</code> (or filename). Use <code className="font-mono bg-muted px-1 rounded">## Section</code> for Preparation / Please tell us / Afterwards blocks.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Default modality</span>
                <select value={modality} onChange={e => setModality(e.target.value)}
                  data-testid="bulk-prep-modality"
                  className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
                  {PREP_MODALITIES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Default subcategory</span>
                <select value={subcategory} onChange={e => setSubcategory(e.target.value)}
                  data-testid="bulk-prep-subcategory"
                  className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
                  {PREP_SUBCATEGORIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>

            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Default booking alerts (apply to all)</span>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="bulk-prep-alerts">
                {PREP_TAG_OPTIONS.map(t => {
                  const active = alerts.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleAlert(t)}
                      data-testid={`bulk-prep-alert-${t}`}
                      className="text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors"
                      style={active
                        ? { backgroundColor: `${M.color}15`, borderColor: `${M.color}40`, color: M.color }
                        : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="block border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary/60 cursor-pointer transition-colors" data-testid="bulk-prep-dropzone">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <span className="block text-sm font-semibold text-foreground">Click to pick markdown files</span>
              <span className="block text-[11px] text-muted-foreground mt-1">.md · .markdown · .txt · Multi-select supported</span>
              <input type="file" accept=".md,.markdown,.txt,text/markdown,text/plain"
                multiple
                data-testid="bulk-prep-file-input"
                className="hidden"
                onChange={e => { onPick(e.target.files); e.target.value = ''; }} />
            </label>

            {files.length > 0 && (
              <div className="border border-border rounded-xl overflow-hidden" data-testid="bulk-prep-file-list">
                <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {files.length} file{files.length === 1 ? '' : 's'} queued · {includedCount} included
                  </span>
                </div>
                <ul className="max-h-72 overflow-y-auto">
                  {files.map((f, idx) => (
                    <li key={idx} data-testid={`bulk-prep-row-${idx}`}
                      className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2 border-b border-border/40 last:border-0">
                      <input type="checkbox" checked={f.include} disabled={importing}
                        onChange={() => updateFile(idx, { include: !f.include })}
                        data-testid={`bulk-prep-include-${idx}`}
                        className="w-4 h-4 rounded border-border" />
                      <div className="min-w-0">
                        <input type="text" value={f.title} disabled={importing}
                          onChange={e => updateFile(idx, { title: e.target.value })}
                          data-testid={`bulk-prep-title-${idx}`}
                          className="w-full text-sm bg-transparent border-0 border-b border-transparent focus:border-border focus:outline-none text-foreground font-medium" />
                        <p className="text-[11px] text-muted-foreground truncate">
                          {f.name} · {(f.content.length / 1024).toFixed(1)} KB
                          {f.status === 'success' && <span className="ml-2 text-green-600 font-semibold">Imported</span>}
                          {f.status === 'failed' && <span className="ml-2 text-red-500 font-semibold">{f.error}</span>}
                        </p>
                      </div>
                      <button type="button" onClick={() => removeFile(idx)} disabled={importing}
                        data-testid={`bulk-prep-remove-${idx}`}
                        className="p-1.5 text-muted-foreground hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={onClose} disabled={importing}
                className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
              <button type="button" onClick={runImport}
                disabled={importing || includedCount === 0}
                data-testid="bulk-prep-submit"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: M.color }}>
                <Upload className="w-3.5 h-3.5" /> {importing ? `Importing… (${files.filter(f => f.status === 'success').length}/${includedCount})` : `Import ${includedCount} guide${includedCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3" data-testid="bulk-prep-result">
              <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-green-600">Imported</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{successCount}</p>
              </div>
              <div className="p-4 rounded-xl border border-red-500/30 bg-red-500/5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-red-600">Failed</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{failedCount}</p>
              </div>
            </div>
            {failedCount > 0 && (
              <ul className="border border-border rounded-xl max-h-48 overflow-y-auto" data-testid="bulk-prep-failed-list">
                {files.filter(f => f.status === 'failed').map((f, i) => (
                  <li key={i} className="px-3 py-2 border-b border-border/40 last:border-0">
                    <p className="text-sm font-semibold text-foreground">{f.title}</p>
                    <p className="text-[11px] text-red-500">{f.error}</p>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={onDone}
                data-testid="bulk-prep-done"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white"
                style={{ backgroundColor: M.color }}>
                <CheckCircle2 className="w-3.5 h-3.5" /> Done
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function CustomPrepEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [title, setTitle] = useState(initial?.title || '');
  const [modality, setModality] = useState(initial?.modality || PREP_MODALITIES[0]);
  const [subcategory, setSubcategory] = useState(initial?.subcategory || PREP_SUBCATEGORIES[0]);
  const [alerts, setAlerts] = useState(initial?.alerts || []);
  const [content, setContent] = useState(initial?.content || '## Preparation\n\n- Fast for X hours.\n- Bring referral, Medicare card.\n\n## Please tell us\n\n- If you are pregnant or may be pregnant.\n');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const toggleAlert = (t) => setAlerts(a => a.includes(t) ? a.filter(x => x !== t) : [...a, t]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!title.trim() || !content.trim()) { setErr('Title and content are required.'); return; }
    setSaving(true);
    const payload = {
      title: title.trim(),
      modality,
      subcategory: subcategory === 'Other' ? '' : subcategory,
      content,
      alerts,
      sort_order: null,
    };
    try {
      if (isNew) {
        await axios.post(`${API}/prep-guides`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/prep-guides/${initial.guide_id}`, payload, { withCredentials: true });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <Modal title={isNew ? 'Create prep guide' : 'Edit prep guide'} onClose={onClose} size="lg">
      <form onSubmit={submit} className="space-y-4" data-testid="custom-prep-editor">
        <Input label="Exam / procedure title (e.g. 'MRI Knee')" value={title} onChange={setTitle} required testid="custom-prep-title" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Modality</span>
            <select value={modality} onChange={e => setModality(e.target.value)}
              data-testid="custom-prep-modality"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
              {PREP_MODALITIES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Subcategory</span>
            <select value={subcategory} onChange={e => setSubcategory(e.target.value)}
              data-testid="custom-prep-subcategory"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
              {PREP_SUBCATEGORIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>

        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Booking alerts</span>
          <p className="text-[11px] text-muted-foreground mb-2">Tick the flags reception must see for this exam.</p>
          <div className="flex flex-wrap gap-1.5" data-testid="custom-prep-alerts">
            {PREP_TAG_OPTIONS.map(t => {
              const active = alerts.includes(t);
              return (
                <button key={t} type="button"
                  onClick={() => toggleAlert(t)}
                  data-testid={`custom-prep-alert-${t}`}
                  className="text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors"
                  style={active
                    ? { backgroundColor: `${M.color}15`, borderColor: `${M.color}40`, color: M.color }
                    : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Markdown content</span>
          <p className="text-[11px] text-muted-foreground mb-1">
            Use <code className="font-mono bg-muted px-1 rounded">##&nbsp;Heading</code> for sections (e.g. "Preparation", "Please tell us", "Afterwards").
          </p>
          <textarea value={content} onChange={e => setContent(e.target.value)}
            required rows={12}
            data-testid="custom-prep-content"
            className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 font-mono leading-relaxed" />
        </div>

        {err && <p className="text-xs text-red-500" data-testid="custom-prep-editor-error">{err}</p>}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
          <button type="submit" disabled={saving}
            data-testid="custom-prep-save"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: M.color }}>
            <CheckCircle2 className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (isNew ? 'Create guide' : 'Save changes')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Screening submissions dashboard
// Admin / Superuser only. Lists every patient-filled screening submission
// with form title, patient name, flag count and status. Click to drill into a
// submission detail modal with every Q/A, consent signature, and status updater.
// ─────────────────────────────────────────────────────────────────────────────

// Incident category / status / severity dictionaries used by the dashboard
// widget below. Mirrors the backend incident enums in routes/incidents.py.
const INC_CATEGORIES = [
  { value: '',                  label: 'All categories' },
  { value: 'near_miss',         label: 'Near miss' },
  { value: 'patient_safety',    label: 'Patient safety' },
  { value: 'complaint',         label: 'Complaint' },
  { value: 'equipment',         label: 'Equipment' },
  { value: 'staff_injury',      label: 'Staff injury' },
  { value: 'infection_breach',  label: 'Infection breach' },
  { value: 'medication',        label: 'Medication' },
  { value: 'radiation',         label: 'Radiation' },
  { value: 'communication',     label: 'Communication' },
  { value: 'it_system',         label: 'IT / system' },
  { value: 'other',             label: 'Other' },
];
const INC_STATUSES = [
  { value: '',                  label: 'Any status' },
  { value: 'reported',          label: 'Reported', color: '#F59E0B' },
  { value: 'under_review',      label: 'Under review', color: '#2563EB' },
  { value: 'rca_in_progress',   label: 'RCA in progress', color: '#7C3AED' },
  { value: 'closed',            label: 'Closed', color: '#16A34A' },
];
const INC_SEVERITIES = {
  near_miss: { label: 'Near miss', color: '#16A34A' },
  minor:     { label: 'Minor', color: '#F59E0B' },
  moderate:  { label: 'Moderate', color: '#D97706' },
  major:     { label: 'Major', color: '#DC2626' },
};

function MonthlyAuditWidget() {
  const [runs, setRuns] = useState(null);
  const [period, setPeriod] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [openRun, setOpenRun] = useState(null);   // run_id currently expanded

  const load = async () => {
    try {
      const r = await axios.get(`${API}/audits/monthly`, { withCredentials: true });
      setRuns(r.data.runs || []);
      setPeriod(r.data.period);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load audit runs.');
    }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/audits/monthly/seed`, {}, { withCredentials: true });
      await load();
    } finally { setBusy(false); }
  };

  const toggle = async (run, item) => {
    // Optimistic update — flip locally, confirm with the server.
    const nextChecked = !item.checked;
    setRuns(rs => rs.map(r => r.run_id === run.run_id
      ? { ...r, items: r.items.map(it => it.key === item.key ? { ...it, checked: nextChecked } : it) }
      : r));
    try {
      const resp = await axios.patch(
        `${API}/audits/monthly/${run.run_id}/items/${item.key}`,
        { checked: nextChecked },
        { withCredentials: true }
      );
      setRuns(rs => rs.map(r => r.run_id === run.run_id ? resp.data : r));
    } catch (e) {
      // Roll back on failure
      setRuns(rs => rs.map(r => r.run_id === run.run_id
        ? { ...r, items: r.items.map(it => it.key === item.key ? { ...it, checked: item.checked } : it) }
        : r));
      alert(e?.response?.data?.detail || 'Failed to update item.');
    }
  };

  if (runs === null) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 mb-6" data-testid="audit-widget-loading">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading monthly walk-through…
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 mb-6" data-testid="audit-widget" id="audits">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="inline-flex items-center gap-2 mb-1">
            <ClipboardCheck className="w-4 h-4" style={{ color: M.color }} />
            <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: M.color }}>
              Monthly walk-through · {period}
            </span>
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-foreground">Patient-privacy audit</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-lg">
            Tap each item as you walk each room. Ticks are stamped with your name and time for the DIAS trail.
          </p>
        </div>
        {runs.length === 0 && (
          <button onClick={seed} disabled={busy} data-testid="audit-seed"
            className="text-sm font-semibold px-3 py-2 rounded-lg border hover:bg-muted transition inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FilePlus2 className="w-4 h-4" />}
            Seed this month
          </button>
        )}
      </div>
      {error && <div className="text-xs text-red-600 mb-2">{error}</div>}
      {runs.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">
          No walk-through has been seeded for {period} yet. Tap <em>Seed this month</em> to start.
        </div>
      ) : (
        <div className="space-y-3">
          {runs.map(run => {
            const ticked = run.items.filter(i => i.checked).length;
            const total = run.items.length;
            const done = run.completed_at != null;
            const isOpen = openRun === run.run_id;
            return (
              <div key={run.run_id} className="rounded-xl border border-border overflow-hidden" data-testid={`audit-run-${run.site_id || 'practice'}`}>
                <button onClick={() => setOpenRun(isOpen ? null : run.run_id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition text-left"
                  data-testid={`audit-run-toggle-${run.site_id || 'practice'}`}>
                  <div>
                    <div className="text-sm font-bold text-foreground">{run.site_name || 'Practice-wide'}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {ticked}/{total} items ticked {done && <span className="ml-2 text-green-600 font-semibold">· complete</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full transition-all"
                        style={{ width: `${(ticked / total) * 100}%`, backgroundColor: done ? '#16a34a' : M.color }} />
                    </div>
                    <span className="text-xs text-muted-foreground">{isOpen ? '▲' : '▼'}</span>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-border">
                    {run.items.map(item => (
                      <button key={item.key} onClick={() => toggle(run, item)}
                        data-testid={`audit-item-${run.site_id || 'practice'}-${item.key}`}
                        className={`w-full flex items-start gap-3 text-left px-4 py-3 border-t border-border hover:bg-muted/40 transition ${item.checked ? 'bg-emerald-50/50 dark:bg-emerald-950/10' : ''}`}>
                        {item.checked
                          ? <CheckSquare className="w-5 h-5 shrink-0 text-emerald-600" />
                          : <Square className="w-5 h-5 shrink-0 text-muted-foreground" />}
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm ${item.checked ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                            {item.label}
                          </div>
                          {item.checked && item.checked_by && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              ticked by {item.checked_by} · {(item.checked_at || '').slice(0, 16).replace('T', ' ')}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



function IncidentsDashboardWidget() {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [trendMonths, setTrendMonths] = useState(12);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (catFilter) params.append('category', catFilter);
      if (statusFilter) params.append('status', statusFilter);
      const [s, r, t] = await Promise.all([
        axios.get(`${API}/incidents/stats`, { withCredentials: true }),
        axios.get(`${API}/incidents?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/incidents/trends?months=${trendMonths}`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setRows(r.data);
      setTrends(t.data);
    } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [catFilter, statusFilter, trendMonths]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : '—';
  const catName = (v) => INC_CATEGORIES.find(c => c.value === v)?.label || v;
  const statusMeta = (v) => INC_STATUSES.find(s => s.value === v) || { label: v, color: '#64748B' };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-10" data-testid="mgmt-incidents">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" style={{ color: M.color }} />
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Incident register</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Every filed report. Open a row to triage, run RCA and close. Near-miss reporting is a leading safety indicator — encourage it.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
            data-testid="incidents-cat-filter"
            className="text-xs bg-background border border-border rounded-lg px-2 py-1.5">
            {INC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            data-testid="incidents-status-filter"
            className="text-xs bg-background border border-border rounded-lg px-2 py-1.5">
            {INC_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Headline KPIs */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="incidents-stats">
          <Kpi label="Total" value={stats.total} color={M.color} />
          <Kpi label="Open" value={stats.open} color="#F59E0B" />
          <Kpi label="Near misses" value={stats.near_miss} color="#16A34A"
            helper="Leading safety indicator" />
          <Kpi label="Major events" value={stats.major} color={stats.major > 0 ? '#DC2626' : '#64748B'} />
          <Kpi label="Last 30 days" value={stats.last_30_days} color="#2563EB" />
        </div>
      )}

      {/* Trends chart */}
      {trends && <IncidentTrendsChart trends={trends} months={trendMonths} onMonthsChange={setTrendMonths} />}

      {loading ? (
        <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-border rounded-xl" data-testid="incidents-empty">
          <p className="text-sm text-muted-foreground">No incidents match this filter.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="incidents-table">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Severity</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Submitted by</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(i => {
                const sv = INC_SEVERITIES[i.severity] || { label: i.severity, color: '#64748B' };
                const st = statusMeta(i.status);
                return (
                  <tr key={i.incident_id} className="border-b border-border/60 last:border-0"
                    data-testid={`incident-row-${i.incident_id}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmt(i.submitted_at)}</td>
                    <td className="px-3 py-2 text-foreground font-medium">{catName(i.category)}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${sv.color}15`, color: sv.color }}>{sv.label}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{i.location || '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {i.anonymous ? (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Lock className="w-3 h-3" /> Anonymous
                        </span>
                      ) : (
                        <span className="text-foreground">{i.submitter?.name || '—'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${st.color}15`, color: st.color }}>{st.label}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/incidents/${i.incident_id}`}
                        data-testid={`incident-open-${i.incident_id}`}
                        className="text-[10px] font-semibold px-2 py-1 rounded-md border border-border hover:bg-accent">
                        Open
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, color, helper }) {
  return (
    <div className="p-4 rounded-xl border border-border bg-card">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-['Outfit'] text-2xl font-bold mt-1" style={{ color }}>{value}</p>
      {helper && <p className="text-[10px] text-muted-foreground mt-0.5">{helper}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Consent submissions dashboard (admin)
// Lists every captured consent. Click → /consent/submission/:id where PMs can
// countersign, print / export the PDF, and delete.
// ─────────────────────────────────────────────────────────────────────────────
function IncidentTrendsChart({ trends, months, onMonthsChange }) {
  const data = (trends.buckets || []).map(b => {
    const [y, m] = b.month.split('-');
    const label = new Date(Number(y), Number(m) - 1, 1)
      .toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
    return { label, total: b.total, near_miss: b.near_miss, major: b.major };
  });
  const hasData = data.some(d => d.total > 0);

  return (
    <div className="bg-card border border-border rounded-2xl p-5 mb-5" data-testid="incidents-trends">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: M.color }} />
            <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">Incident trends</h3>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Monthly reports over the selected window. A healthy reporting culture shows <span className="font-semibold" style={{ color: '#16A34A' }}>near-miss counts rising</span> and <span className="font-semibold" style={{ color: '#DC2626' }}>major events flat or falling</span>.
          </p>
        </div>
        <div className="flex items-center gap-1" data-testid="trends-window">
          {[6, 12, 24].map(m => (
            <button key={m} onClick={() => onMonthsChange(m)}
              data-testid={`trends-window-${m}`}
              className="text-[10px] font-semibold px-2 py-1 rounded-md border transition-colors"
              style={months === m
                ? { backgroundColor: `${M.color}15`, borderColor: `${M.color}40`, color: M.color }
                : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
              {m}m
            </button>
          ))}
        </div>
      </div>
      {!hasData ? (
        <div className="h-48 flex items-center justify-center border border-dashed border-border rounded-xl" data-testid="trends-empty">
          <p className="text-xs text-muted-foreground">No incidents in the last {months} month{months === 1 ? '' : 's'}.</p>
        </div>
      ) : (
        <div style={{ width: '100%', height: 220 }}>
          <RC.ResponsiveContainer>
            <RC.BarChart data={data} margin={{ top: 10, right: 10, left: -12, bottom: 0 }}>
              <RC.CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <RC.XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <RC.YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} width={30} />
              <RC.Tooltip
                cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }} />
              <RC.Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
              <RC.Bar dataKey="total" name="All reports" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              <RC.Bar dataKey="near_miss" name="Near miss" fill="#16A34A" radius={[3, 3, 0, 0]} />
              <RC.Bar dataKey="major" name="Major" fill="#DC2626" radius={[3, 3, 0, 0]} />
            </RC.BarChart>
          </RC.ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

